/** The Canvas chart: candles, VWAP and the server's levels; developing bars are hollow and dashed (U031, U032). */
import type { Levels } from "../../contracts/views.ts";
import type { Series } from "./model.ts";

export interface Palette {
  readonly up: string;
  readonly down: string;
  readonly vwap: string;
  readonly level: string;
  readonly text: string;
}

export const LIGHT: Palette = {
  up: "#0b6b3a",
  down: "#a31515",
  vwap: "#1f4fbf",
  level: "#6b3fa0",
  text: "#222222",
};
export const DARK: Palette = {
  up: "#4cc38a",
  down: "#ff7b72",
  vwap: "#79a8ff",
  level: "#c297ff",
  text: "#e6e6e6",
};

export interface Size {
  readonly width: number;
  readonly height: number;
}

const LABELLED: readonly (readonly [keyof Levels, string])[] = [
  ["openingRangeHighPaise", "OR high"],
  ["openingRangeLowPaise", "OR low"],
  ["entryPaise", "Entry"],
  ["stopPaise", "Stop"],
  ["exitPaise", "Exit"],
];

/** Draw the series on a 2D context; prices are scaled to pixels only for drawing. */
export function drawChart(
  ctx: CanvasRenderingContext2D,
  series: Series,
  levels: Levels,
  size: Size,
  palette: Palette,
): void {
  const pad = 24;
  const span = Number(series.maxPaise - series.minPaise) || 1;
  const y = (price: bigint): number =>
    pad + (1 - Number(price - series.minPaise) / span) * (size.height - 2 * pad);
  const step = (size.width - 2 * pad) / Math.max(1, series.points.length);
  const x = (index: number): number => pad + step * (index + 0.5);
  ctx.clearRect(0, 0, size.width, size.height);
  ctx.font = "12px sans-serif";
  ctx.fillStyle = palette.text;
  ctx.setLineDash([]);

  for (const [key, label] of LABELLED) {
    const value = levels[key];
    if (value !== null) {
      ctx.strokeStyle = palette.level;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(pad, y(value));
      ctx.lineTo(size.width - pad, y(value));
      ctx.stroke();
      ctx.fillStyle = palette.text;
      ctx.fillText(label, pad + 2, y(value) - 3);
    }
  }
  series.points.forEach((point, index) => {
    const rising = point.closePaise >= point.openPaise;
    const color = rising ? palette.up : palette.down;
    const top = y(point.openPaise > point.closePaise ? point.openPaise : point.closePaise);
    const bottom = y(point.openPaise > point.closePaise ? point.closePaise : point.openPaise);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.setLineDash(point.developing ? [3, 3] : []);
    ctx.beginPath();
    ctx.moveTo(x(index), y(point.highPaise));
    ctx.lineTo(x(index), y(point.lowPaise));
    ctx.stroke();
    const width = Math.max(1, step * 0.6);
    if (point.developing) {
      ctx.strokeRect(x(index) - width / 2, top, width, Math.max(1, bottom - top));
    } else {
      ctx.fillRect(x(index) - width / 2, top, width, Math.max(1, bottom - top));
    }
  });
  ctx.setLineDash([]);
  ctx.strokeStyle = palette.vwap;
  ctx.beginPath();
  let started = false;
  series.points.forEach((point, index) => {
    if (point.vwapPaise === null) {
      return;
    }
    if (started) {
      ctx.lineTo(x(index), y(point.vwapPaise));
    } else {
      ctx.moveTo(x(index), y(point.vwapPaise));
      started = true;
    }
  });
  ctx.stroke();
}
